/**
 * Utility helper to handle exports of Users data to Excel (XLS) and CSV/TXT.
 */

import configCountries from "../../../Config/countries.json";

// Helper to look up country names
const getCountryName = (countryCode) => {
    if (!countryCode) return "";
    const country = configCountries.countries.find(
        (c) => c.value.toLowerCase() === countryCode.toLowerCase()
    );
    return country ? country.label : countryCode;
};

// Helper to get formatted nested values from user object
export const getCellValue = (item, key, t) => {
    switch (key) {
        case "Name":
            return item.Name || "";
        case "Username":
            return item.Username || "";
        case "Email":
            return item.Email || "";
        case "Phone":
            return item.Phone || "";
        case "UserType":
            return item.UserType ? item.UserType.Name : "";
        case "School":
            return item.School ? item.School.Name : "";
        case "Country":
            return getCountryName(item.Country);
        case "LastLogin":
            return item.LastLogin || "";
        case "ActivateUntilDate":
            return item.ActivateUntilDate === "0" ? t("Pages.UsersControl.NotActive") : item.ActivateUntilDate || "";
        case "Status":
            if (item.IsLocked) return t("Pages.UsersControl.Locked");
            return item.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive");
        case "CreationDate":
            return item.CreationDate || "";
        default:
            return "";
    }
};

/**
 * Export data to Excel (XLS) via Tab-Separated Values with UTF-8 BOM.
 */
export const exportToXLS = (data, selectedColumns, filename = "users_export", t) => {
    let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40" dir="rtl">`;
    html += `<head><meta charset="utf-8" /><style>td { mso-number-format:"\\@"; text-align: right; }</style></head><body dir="rtl">`;
    html += `<table border="1"><thead><tr>`;
    
    selectedColumns.forEach(col => {
        html += `<th style="background-color: #f2f2f2; text-align: right; font-weight: bold;">${col.label}</th>`;
    });
    
    html += `</tr></thead><tbody>`;
    
    data.forEach(item => {
        html += `<tr>`;
        selectedColumns.forEach(col => {
            const val = getCellValue(item, col.key, t);
            const cleanVal = String(val !== null && val !== undefined ? val : "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            html += `<td>${cleanVal}</td>`;
        });
        html += `</tr>`;
    });
    
    html += `</tbody></table></body></html>`;

    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel;charset=utf-8" });
    triggerDownload(blob, `${filename}.xls`);
};

/**
 * Export data to CSV/TXT with standard comma-separation and UTF-8 BOM.
 */
export const exportToCSV = (data, selectedColumns, filename = "users_export", t) => {
    const BOM = "\ufeff";
    const headers = selectedColumns.map(col => `"${col.label.replace(/"/g, '""')}"`).join(",");
    const rows = data.map(item => {
        return selectedColumns.map(col => {
            const val = getCellValue(item, col.key, t);
            return `"${String(val).replace(/"/g, '""').replace(/[\n\r]/g, " ")}"`;
        }).join(",");
    }).join("\n");

    const fileContent = BOM + headers + "\n" + rows;
    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${filename}.csv`);
};

/**
 * Trigger browser file download
 */
const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
